import React, { useEffect, useRef, useState } from "react";
import { classNames } from "../../helpers/classNames";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import FileTransfer from "../../assets/lottie/file-transfer.json";
import doc from "../../assets/lottie/doc.json";
import Button from "./Button";

const FileUploadSingle = ({
  required,
  value,
  onRemove = () => {},
  onChange = () => {},
  labelClasses,
  label,
  imageName,
  name,
  errorType,
  errorText,
  size = "full",
  placeholder,
  bodyClass,
  accept,
  iconStyleClass,
  uploadSize,
  uploadPercentage = 0,
}) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState({ url: "http://aaa.jpeg" });

  useEffect(() => {
    const url = value instanceof File ? URL.createObjectURL(value) : value;
    if (url !== file) {
      setFile({ url, name: imageName || url });
    }
  }, [value]);

  const handleFileSelect = (event) => {
    const acceptList = accept?.split(",").map((elm) => elm.trim()); // Normalize input
    const file = event.target.files[0];

    if (!file) return;

    if (acceptList === undefined || !acceptList) {
      setFile({ name: file?.name, url: URL.createObjectURL(file) });
      if (file && file instanceof File) {
        onChange(file, event);
      }
      event.target.value = "";
      return;
    }

    const fileExt = file.name.split(".").pop().toLowerCase(); // Extract file extension
    const fileType = file.type; // Get MIME type

    const isValid = acceptList?.some((type) => {
      if (type.startsWith(".")) {
        return fileExt === type.replace(".", "").toLowerCase(); // Match extension
      }
      return fileExt === type || fileType === type; // Match MIME type
    });

    if (!isValid) {
      toast.info(`Upload only ${acceptList?.join(", ")}`);
      event.target.value = "";
      return;
    }
    const fileSizeMB = file.size / (1024 * 1024);
    if (uploadSize && fileSizeMB > uploadSize) {
      toast.warning(`File size must be less than ${uploadSize} MB`);
      event.target.value = "";
      return;
    }

    setFile({ name: file?.name, url: URL.createObjectURL(file) });
    if (file && file instanceof File) {
      onChange(file, event);
    }
    event.target.value = "";
  };

  const handleRemoveFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFile(null);
    onChange("");
    onRemove();
  };

  return (
    <div className={classNames("relative w-full", bodyClass)}>
      {label && (
        <div
          className={
            "text-xs font-medium text-text-disabled mb-1 font-CanelaDeck mb-1 font-CanelaDeck " +
            labelClasses
          }
        >
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-1 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}
      <div className={classNames(size === "full" ? "h-fit" : "h-10")}>
        <label htmlFor="fileUpload" className="relative overflow-hidden">
          <div
            className={classNames(
              "relative overflow-hidden border border-gray-400 flex",
              size === "full"
                ? "flex-col items-center border-2 border-dashed p-6 rounded-3xl h-full w-full bg-background-light"
                : "",
              size === "small"
                ? "h-10 rounded-md py-[2px] px-1 bg-white gap-1"
                : ""
            )}
          >
            {size === "full" && (
              <>
                <div className="w-2/12 h-2/12 mx-auto">
                  <Lottie animationData={FileTransfer} loop={true} />
                </div>
                <div className="space-y-4 -mt-4 w-full flex flex-col items-center">
                  <div className="font-semibold text-lg text-secondary text-center">
                    {placeholder ||
                      "Drag your documents, photos or videos here to start uploading"}
                  </div>
                  <div className="flex items-center justify-center gap-3 px-10 w-2/3">
                    <div className="bg-gray-300 h-[1px] w-5/12"></div>
                    <span className="text-slate-60 text-secondary uppercase text-sm font-medium">
                      Or
                    </span>
                    <div className="bg-gray-300 h-[1px] w-5/12"></div>
                  </div>
                </div>
              </>
            )}
            {size === "small" && (
              <>
                <div className="border border-dashed border-slate-300 flex items-center justify-center w-10 rounded bg-slate-50/50 overflow-hidden">
                  {file?.url ? (
                    <img
                      alt="upload-file"
                      src={file?.url}
                      className="w-full h-full object-cover object-center"
                    />
                  ) : (
                    <Lottie
                      animationData={doc}
                      loop={true}
                      className="scale-[1.8]"
                    />
                  )}
                </div>
                <div
                  className={classNames(
                    "flex flex-col justify-center flex-grow px-2",
                    "max-w-[calc(100%-152px)] font-semibold text-gray-500 truncate whitespace-nowrap"
                  )}
                >
                  {file?.name ? file?.name : file?.url || "Browse for file"}
                </div>
                {file?.url ? (
                  <button
                    type="button"
                    className="text-red-500 hover:bg-red-500/10 transition-all duration-150 w-10 h-full flex items-center justify-center rounded-md ml-auto"
                    onClick={handleRemoveFile}
                  >
                    <i className="fa-fw fa-regular fa-trash-can"></i>
                  </button>
                ) : (
                  <div className="bg-background-light rounded-md flex items-center justify-center w-28 whitespace-nowrap px-3 font-medium text-gray-500">
                    Choose file
                  </div>
                )}

                <div
                  className={classNames(
                    "absolute top-0 left-0 w-full h-full p-[2px]",
                    uploadPercentage === 0 ? "hidden" : "block"
                  )}
                >
                  <div
                    className={classNames(
                      "transition-[width] duration-500 h-full rounded-r-md text-center flex items-center justify-center",
                      uploadPercentage === "error"
                        ? "bg-red-500/10"
                        : "bg-green-500/10"
                    )}
                    style={
                      uploadPercentage === "error"
                        ? { width: "100%" }
                        : {
                            width:
                              typeof uploadPercentage === "number" &&
                              `${uploadPercentage}%`,
                          }
                    }
                  ></div>
                </div>
              </>
            )}
            {size === "small" && !file?.url && (
              <input
                ref={inputRef}
                id="fileUpload"
                type="file"
                className="w-full h-full opacity-0 bg-red-500/20 absolute top-0 left-0"
                name={name}
                onChange={handleFileSelect}
              />
            )}
            {size === "full" && (
              <>
                <input
                  ref={inputRef}
                  id="fileUpload"
                  type="file"
                  className="w-full h-full opacity-0 bg-red-500/20 absolute top-0 left-0"
                  name={name}
                  onChange={handleFileSelect}
                />
                <Button
                  buttonFunction={() => inputRef.current?.click()}
                  buttonClasses={"px-16 mt-5"}
                  buttonLabel={"Browse file"}
                />
              </>
            )}
            {size === "full" && file?.url && (
              <div className="relative z-[1] group rounded-md bg-white h-16 mt-4 w-96 p-2 flex items-center justify-between gap-2">
                <div className="border border-slate-300 border-dashed overflow-hidden rounded-md min-w-14 w-14 h-full">
                  <img
                    alt="upload-file"
                    src={file?.url}
                    className="w-full h-full object-cover object-center group-hover:scale-[1.2] transition-all duration-300"
                  />
                </div>
                <div className="h-full flex-grow overflow-hidden max-w-[calc(100%-88px)] flex items-center">
                  <div className="truncate whitespace-nowrap w-full">
                    {file?.name || file?.url}
                  </div>
                </div>
                <div className="h-full w-8 flex items-center justify-center">
                  <button
                    type="button"
                    className="text-red-500 hover:bg-red-500/10 transition-all duration-150 h-8 w-8 rounded-md"
                    onClick={handleRemoveFile}
                  >
                    <i className="fa-fw fa-regular fa-trash-can"></i>
                  </button>
                </div>
                <div
                  className={classNames(
                    "absolute bottom-0 left-0 w-full h-[6px] p-[2px]",
                    uploadPercentage === 0 ? "hidden" : "block"
                  )}
                >
                  <div
                    className={classNames(
                      "transition-[width] duration-500 h-full rounded-r-md text-center flex items-center justify-center",
                      uploadPercentage === "error"
                        ? "bg-red-500"
                        : "bg-green-500"
                    )}
                    style={
                      uploadPercentage === "error"
                        ? { width: "100%" }
                        : {
                            width:
                              typeof uploadPercentage === "number" &&
                              `${uploadPercentage}%`,
                          }
                    }
                  ></div>
                </div>
              </div>
            )}
          </div>
        </label>
      </div>
      {errorType && errorType === "danger" && (
        <div className="text-xs font-medium text-red-600">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "warning" && (
        <div className="text-xs font-medium text-amber-500">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "success" && (
        <div className="text-xs font-medium text-green-600">
          <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "info" && (
        <div className="text-xs font-medium text-sky-600">
          <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
          {errorText}
        </div>
      )}
    </div>
  );
};

export default FileUploadSingle;
